import flatten from 'lodash/flatten';
import { JSX, useEffect, useState } from 'react';

import { GET_ANIMATIONS } from '@repo/api-core-toolkit';
import { Character } from '@repo/avatars';
import { Animation } from '@repo/database-core';
import { RenderableAvatar } from '@repo/types-client';
import { useDatabaseQuery } from '@repo/utils';

import { tw } from '../../utils';

interface AvatarProps {
  width?: number;
  height?: number;
  data: RenderableAvatar;
  loadingElement?: JSX.Element;
  errorElement?: JSX.Element;
  filterAnimations?: (poseAnimations: Animation[]) => Animation[];
  showDoodleName?: boolean;
  topPosition?: string;
}

export const Avatar: React.FunctionComponent<AvatarProps> = ({
  width,
  height,
  data,
  loadingElement = <div>Loading Avatar...</div>,
  errorElement = <div>Error loading Avatar.</div>,
  filterAnimations,
  showDoodleName = false,
  topPosition,
}: AvatarProps) => {
  const [avatarData, setAvatarData] = useState<RenderableAvatar | null>(null);

  const {
    data: poseData,
    isLoading: loadingPoseData,
    error: errorPoseData,
  } = useDatabaseQuery({
    queryKey: ['animation'],
    query: GET_ANIMATIONS as any,
    variables: {
      where: {
        type: {
          in: ['BODY'],
        },
      },
    },
    field: 'getAnimations',
  });

  useEffect(() => {
    setAvatarData(data);
  }, [data]);

  if (loadingPoseData) {
    return loadingElement;
  }

  if (errorPoseData || !avatarData) {
    return errorElement;
  }

  return (
    <Character
      className={tw('relative', topPosition ? topPosition : '-top-[35px]')}
      width={width}
      height={height}
      baseAnimations={filterAnimations ? filterAnimations(poseData) : poseData}
      currentHairPinch={avatarData.hairPinch}
      currentHairColor={avatarData.hairColor}
      currentSkin={avatarData.skinTone}
      equippedAnimations={flatten(
        avatarData.equippedWearables?.map((wearable) => wearable.animations),
      )}
      animationsToHide={flatten(
        avatarData.equippedWearables?.map(
          (wearable) => wearable.animationsToHide,
        ),
      )}
      name={showDoodleName ? avatarData.name : null}
    />
  );
};
